import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { IErrorModel, IPaginatedResponse } from '@core/models/generic.model';
import bullkService from 'services/api/bullk.service';

// -------------------------------------------------------------------------

interface IBullkSliceState {
    loading: boolean;
    acceptOrRejectLoading: boolean;
    error: IErrorModel | undefined;
    bullkRequests: IPaginatedResponse | null;
}

const initialState: IBullkSliceState = {
    loading: false,
    acceptOrRejectLoading: false,
    error: {} as IErrorModel,
    bullkRequests: null,
};

// -------------------------------------------------------------------------

export const getBullkThunk = createAsyncThunk<
    IPaginatedResponse,
    { pageSize: number; pageNumber: number },
    { rejectValue: IErrorModel }
>(`/bullk/getBullk`, async (params, { rejectWithValue }: any) => {
    try {
        const response: any = await bullkService.getBullk(params);
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch bullk data', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

// -------------------------------------------------------------------------

export const acceptOrRejectBullkThunk = createAsyncThunk<
    any,
    { id: number, body: { proposedUnitPrice: string; sellerNote: string; status: string; frequency: string } },
    { rejectValue: IErrorModel }
>(`/bullk/acceptOrRejectBullk`, async ({ id, body }, { rejectWithValue }: any) => {
    try {
        const response: any = await bullkService.acceptOrRejectBullk(id, body);
        toast.success(response.data.message || 'Bullk request accepted or rejected successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to accept or reject bullk data', { autoClose: 5000 });
        return rejectWithValue(err);
    }
}
);

// -------------------------------------------------------------------------


const bullkSlice = createSlice({
    name: `bullk`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Get Bullk Requests
        builder.addCase(getBullkThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getBullkThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.bullkRequests = action.payload;
        });
        builder.addCase(getBullkThunk.rejected, (state, action) => {
            state.loading = false;
            state.bullkRequests = null;
            state.error = action.payload;
        });
        // Accept or Reject Bullk Request
        builder.addCase(acceptOrRejectBullkThunk.pending, (state) => {
            state.acceptOrRejectLoading = true;
        });
        builder.addCase(acceptOrRejectBullkThunk.fulfilled, (state: any, action: any) => {
            state.acceptOrRejectLoading = false;
        });
        builder.addCase(acceptOrRejectBullkThunk.rejected, (state, action) => {
            state.acceptOrRejectLoading = false;
            state.error = action.payload;
        });
    }
});

// -------------------------------------------------------------------------

export default bullkSlice;
export const bullkReducer = bullkSlice.reducer;

